// The browser back gesture closes the overlay that is open instead of leaving
// the page underneath it. On a phone the back swipe is the player's first
// reach for "get me out of this", so every modal, sheet and card that covers
// the screen calls this hook.
//
// While open, the hook holds one history entry of its own. Back pops it and
// `onClose` runs. When the overlay is closed any other way (its button, Escape,
// a choice inside it) the hook drops its entry again with a single history.back(),
// unless the page has already navigated on, in which case the entry is simply
// orphaned below the new one. Entries are kept on a module stack so overlays
// nest and one pop closes one overlay.
//
// useLeaveLectern holds the live argument on its entry with its own popstate
// handler. It asks `popBelongsToOverlay(e)` first, so a pop that belongs to an
// overlay closes only the overlay. Both listeners may run in either order; the
// verdict is computed once per event and cached.
import { useEffect, useRef } from 'react';

interface Entry {
  id: number;
  /** Null once the owner has closed by other means. */
  close: (() => void) | null;
  /** history.back() has been issued for this closed entry. */
  dropping: boolean;
  timer: number | null;
}

const MARKER = '__overlayEntry';
const stack: Entry[] = [];
// Overlays that opened while a drop was still travelling: pushing before the
// traversal lands would have it undo the new entry (WebKit lands it late), so
// they wait here and are pushed once it has.
const waiting: Entry[] = [];
let inflight = 0;
let inflightCap: number | null = null;
// A history entry held below the overlays (useLeaveLectern's) is to be given back once none is open.
// The page (pathname) that owes one back below its overlays, or null. Tied to the
// page so a debt can never be paid on another one: whatever path an overlay is
// released by after the player navigates on, a stale debt is simply dropped
// (cross-model review of dddd04ac, 2026-10-09).
let extraBackOwedOn: string | null = null;
const verdicts = new WeakMap<Event, boolean>();
let nextId = 1;
let listening = false;

function flushWaiting(): void {
  if (inflight > 0) return;
  for (const entry of waiting.splice(0)) {
    if (!entry.close) continue;
    window.history.pushState({ ...(window.history.state ?? {}), [MARKER]: entry.id }, '', window.location.href);
    stack.push(entry);
  }
}

/** history.back() for an entry the owner has let go of; its popstate settles it. */
function goBack(): void {
  inflight += 1;
  // A traversal that never reports (a blocked history) must not hold the waiting overlays for ever.
  if (inflightCap != null) window.clearTimeout(inflightCap);
  inflightCap = window.setTimeout(() => { inflight = 0; inflightCap = null; flushWaiting(); }, 600);
  window.history.back();
}

function dropClosedTop(): void {
  const top = stack[stack.length - 1];
  if (!top || top.close || top.dropping) return;
  top.dropping = true;
  top.timer = null;
  goBack();
}

function decide(e: Event): boolean {
  const cached = verdicts.get(e);
  if (cached !== undefined) return cached;
  const top = stack.pop();
  const verdict = top !== undefined;
  verdicts.set(e, verdict);
  if (top?.timer != null) window.clearTimeout(top.timer);
  if (top?.dropping && inflight > 0) {
    inflight -= 1;
    if (inflight === 0 && inflightCap != null) { window.clearTimeout(inflightCap); inflightCap = null; }
  }
  if (top?.close) top.close();
  // A closed entry left under the one just popped is stale; step off it too,
  // so the next back press does something visible.
  window.setTimeout(dropClosedTop, 0);
  flushWaiting();
  payExtraBack();
  return verdict;
}

function payExtraBack(): void {
  if (extraBackOwedOn === null || stack.length > 0 || waiting.length > 0 || inflight > 0) return;
  const owedHere = extraBackOwedOn === window.location.pathname;
  extraBackOwedOn = null;
  if (owedHere) window.history.back();
}

/**
 * Give back one history entry that sits BELOW any open overlay (the live
 * argument's guard entry, once the case is over). With nothing open it goes
 * back now; otherwise once the last overlay has closed, so the back() cannot
 * pop an overlay's entry in its place and leave the guard's behind.
 */
export function backBelowOverlays(): void {
  extraBackOwedOn = window.location.pathname;
  if (stack.length === 0 && waiting.length === 0 && inflight === 0) payExtraBack();
}

/** True when this popstate pops an overlay's entry; the overlay is closed here. */
export function popBelongsToOverlay(e: Event): boolean {
  return decide(e);
}

function ensureListener(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('popstate', (e) => { decide(e); });
}

function open(close: () => void): Entry {
  ensureListener();
  const top = stack[stack.length - 1];
  // Closed and reopened in one tick (StrictMode, a quick toggle): take the
  // entry over rather than racing a back() against a fresh push.
  if (top && !top.close && !top.dropping) {
    if (top.timer != null) window.clearTimeout(top.timer);
    top.timer = null;
    top.close = close;
    return top;
  }
  const entry: Entry = { id: nextId++, close, dropping: false, timer: null };
  if (inflight > 0) { waiting.push(entry); return entry; }
  window.history.pushState({ ...(window.history.state ?? {}), [MARKER]: entry.id }, '', window.location.href);
  stack.push(entry);
  return entry;
}

function release(entry: Entry): void {
  if (!entry.close) return;
  entry.close = null;
  const top = stack[stack.length - 1];
  if (top !== entry) return;
  const current = window.history.state as Record<string, unknown> | null;
  if (current?.[MARKER] !== entry.id) {
    // The page navigated on; the entry is below the new one and cannot be
    // dropped, and neither can any entry under it: every one belongs to the page
    // the player left. Forget them all, or the next overlay on the new page would
    // reuse a dead entry instead of pushing its own.
    stack.length = 0;
    // Nor can an owed guard entry: going back now would leave the page the player moved to.
    extraBackOwedOn = null;
    return;
  }
  entry.timer = window.setTimeout(() => {
    entry.timer = null;
    entry.dropping = true;
    goBack();
  }, 0);
}

/**
 * Drop the top overlay's history entry now and resolve once the traversal has
 * landed. For an overlay whose close is followed by a navigation of its own
 * that happens later (the name prompt starts a game after an await): a
 * navigation pushed before the pending back() lands would be undone by it, so
 * the owner drops the entry first and navigates after. The overlay's own close
 * callback is not called.
 */
export function dropOverlayEntry(): Promise<void> {
  const top = stack[stack.length - 1];
  if (!top || top.dropping) return Promise.resolve();
  if (top.timer != null) window.clearTimeout(top.timer);
  top.timer = null;
  top.close = null;
  top.dropping = true;
  return new Promise<void>((resolve) => {
    const done = () => {
      window.removeEventListener('popstate', done);
      window.clearTimeout(cap);
      resolve();
    };
    // A traversal that never reports (a blocked history) must not hold the caller.
    const cap = window.setTimeout(done, 500);
    window.addEventListener('popstate', done);
    goBack();
  });
}

export function useBackToClose(open_: boolean, onClose: () => void): void {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open_) return;
    const entry = open(() => closeRef.current());
    return () => release(entry);
  }, [open_]);
}

/** Test seam: forget every entry. */
export function resetBackToClose(): void {
  stack.length = 0;
  waiting.length = 0;
  inflight = 0;
  extraBackOwedOn = null;
  if (inflightCap != null) window.clearTimeout(inflightCap);
  inflightCap = null;
}
